import { useState } from "react";

import { EntranceTypes, getEntranceType } from "../../helpers/helpers";
import { useQueryParams } from "../../helpers/hooks/useLocation";
import { handleLinkTracking } from "../../helpers/observability/analytics";
import appSettings from "../../settings/appSettings";
import { getAllViews, getTotalPages } from "../../utils/formFlowUtils";
import { FormFlowContext } from "./FormFlowContext";

/**
 * Provides context and state management for the form flow.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to be rendered within the provider.
 * @returns {JSX.Element} The FormFlowContext provider with the form flow state and handlers.
 *
 * @typedef {Object} UserData
 * @property {string} email - The user's email address.
 * @property {string} firstName - The user's first name.
 * @property {string} lastName - The user's last name.
 * @property {string} supporterNumber - The user's supporter number.
 *
 * @typedef {Object} ContextValue
 * @property {boolean} auth0OnlyExperience - Indicates if the user has only an Auth0 account.
 * @property {boolean} bothAccountsExperience - Indicates if the user has both Bettermode and Auth0 accounts.
 * @property {number} currentPage - The current page number in the form flow.
 * @property {string} currentView - The current view in the form flow.
 * @property {string} entranceExperience - The type of entrance experience for the user.
 * @property {number} finalSurveyPage - The final page number of the survey.
 * @property {boolean} formError - Indicates if there is an error in the form submission.
 * @property {boolean} formSuccess - Indicates if the form was successfully submitted.
 * @property {boolean} noAccountsExperience - Indicates if the user has neither Bettermode nor Auth0 accounts.
 * @property {boolean} prepopulateComplete - Indicates if prepopulation of data is complete.
 * @property {string} submittingMessage - The message displayed during form submission.
 * @property {number} totalQuestions - The total number of questions in the survey.
 * @property {boolean} noHandshake - Indicates if there was no handshake during the process.
 * @property {UserData} userData - The user's data.
 * @property {Function} pageBackward - Function to navigate to the previous page.
 * @property {Function} pageForward - Function to navigate to the next page.
 * @property {Function} setCurrentPage - Function to set the current page number.
 * @property {Function} setFormError - Function to set the form error state.
 * @property {Function} setFormSuccess - Function to set the form success state.
 * @property {Function} setSubmittingMessage - Function to set the submitting message.
 * @property {Function} setNoHandshake - Function to set the no handshake state.
 * @property {Function} setUserData - Function to set the user data.
 * @property {Function} setView - Function to set the current view.
 */
export const FormFlowProvider = ({ children }) => {
  const { getValueFromParams } = useQueryParams();
  const screenParamValue = getValueFromParams("screen");

  const { surveyConfig, badges } = appSettings;
  const { surveyQuestions = [] } = surveyConfig;
  const badgesExist = badges.length > 0;
  const surveyQuestionsExist = surveyQuestions.length > 0;

  const initialView = screenParamValue ?? "welcome";
  // the welcome page is page 1 for users with neither a Bettermode account nor an Auth0 account
  // the signin page is page 1 for users with both accounts already
  const [currentView, setView] = useState(initialView);
  const [currentPage, setCurrentPage] = useState(1);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState();
  const [submittingMessage, setSubmittingMessage] = useState("");
  const [noHandshake, setNoHandshake] = useState(false);
  const [prepopulateComplete, setPrepopulateComplete] = useState(false);

  /** userData: { email, firstName, lastName, supporterNumber } */
  const [userData, setUserData] = useState();
  const entranceExperience = getEntranceType(userData, screenParamValue, noHandshake);

  const { numQuestionPages: totalQuestionPages, numTotalExperiencePages: totalPages } =
    getTotalPages(entranceExperience, badgesExist, surveyQuestions.length);

  const getViewInfo = () => {
    const allViews = getAllViews(badgesExist, surveyQuestionsExist, entranceExperience);
    return allViews[currentView];
  };

  const pageForward = (endOfQuestions = false) => {
    handleLinkTracking("next");
    setPrepopulateComplete(true);

    const nextView = getViewInfo(currentView)?.next;

    switch (currentView) {
      case "survey": {
        if (endOfQuestions) {
          setView(nextView);
        }
        setCurrentPage((page) => page + 1);
        break;
      }
      case "signin":
        setSubmittingMessage("Sending email...");
        setCurrentPage((page) => page + 1);
        setView(nextView);
        break;
      case "submitting":
        setView(nextView);
        break;
      default:
        setView(nextView);
        setCurrentPage((page) => page + 1);
        break;
    }
  };

  const pageBackward = () => {
    handleLinkTracking("back");
    const previousView = getViewInfo(currentView)?.previous;

    switch (currentView) {
      case "survey": {
        const firstSurveyPage = badgesExist ? 3 : 2;
        if (currentPage === firstSurveyPage) {
          setView(previousView);
        }
        setCurrentPage((page) => page - 1);
        break;
      }
      case "wrong_info":
        setCurrentPage(1);
        setView("welcome");
        return;
      case "login_issues__signin":
      case "login_issues__one_time_login":
        setView(previousView);
        break;
      default:
        setView(previousView);
        setCurrentPage((page) => page - 1);
    }
  };

  const auth0OnlyExperience = entranceExperience === EntranceTypes.AUTH0_ONLY;
  const noAccountsExperience = entranceExperience === EntranceTypes.NO_ACCOUNTS;
  const bothAccountsExperience = entranceExperience === EntranceTypes.BOTH_ACCOUNTS;

  const readyToImplicitlySubmit = auth0OnlyExperience && currentPage === totalQuestionPages + 1;

  const contextValue = {
    auth0OnlyExperience,
    bothAccountsExperience,
    currentPage,
    currentView,
    entranceExperience,
    formError,
    formSuccess,
    noAccountsExperience,
    noHandshake,
    prepopulateComplete,
    readyToImplicitlySubmit,
    submittingMessage,
    totalPages, // for the progress bar only
    userData,
    pageBackward,
    pageForward,
    setCurrentPage,
    setFormError,
    setFormSuccess,
    setSubmittingMessage,
    setNoHandshake,
    setUserData,
    setView
  };

  return <FormFlowContext.Provider value={contextValue}>{children}</FormFlowContext.Provider>;
};
